import { CdkDragDrop, CdkDropList, copyArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy } from '@angular/core';
import { IconDefinition, faCompressAlt, faExpandAlt, faUpload } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { SkillGroup } from 'src/app/store/samwin/samwin-skills/skill-group';
import {
   AddGreetingItem,
   AddGreetingItems,
   AddGreetingWave,
   RemoveGreetingItem,
   RemoveGreetingItems,
   RemoveGreetingWave,
   SetAgentGreetingDraggedItemKey,
   SetAgentGreetingIsIciDisplayGrouped,
   SetAgentGreetingSkillFilter,
} from 'src/app/store/settings/agent-greeting-settings/agent-greeting-settings.actions';
import { AgentGreetingSettingsSelector } from 'src/app/store/settings/agent-greeting-settings/agent-greeting-settings.selector';
import {
   AgentGreetingItem,
   AgentGreetingItemExpandedState,
   AgentGreetingSettingsState,
   AudioFileInfo,
} from 'src/app/store/settings/agent-greeting-settings/agent-greeting-settings.state';
import { AgentGreetingWaveRemoteDto as AgentGreetingWave } from 'src/generated/signalR/agent-greeting-wave-remote-dto';
import { AudioFile } from '../audio-file';

@Component({
   selector: 'samweb-greeting-settings',
   templateUrl: './greeting-settings.component.html',
   styleUrls: ['./greeting-settings.component.scss'],
})
export class GreetingSettingsComponent implements OnDestroy {
   constructor(public store: Store) {}

   @Select(AgentGreetingSettingsState.wasValuePersisted)
   public wasValuePersisted$: Observable<boolean>;

   @Select(AgentGreetingSettingsSelector.greetingSkills)
   public skillList$: Observable<SkillGroup[]>;

   @Select(AgentGreetingSettingsSelector.availableDropZones)
   public availableDropZones$: Observable<string[]>;

   @Select(AgentGreetingSettingsState.agentGreetingWaves)
   public agentGreetingWaves$: Observable<AudioFileInfo[]>;

   @Select(AgentGreetingSettingsState.filterValue)
   public filterValue$: Observable<string>;

   @Select(AgentGreetingSettingsState.separatedFilterValues)
   public separatedFilterValues$: Observable<string[]>;

   @Select(AgentGreetingSettingsState.draggedItemKey)
   public draggedItemKey$: Observable<AudioFileInfo>;

   @Select(AgentGreetingSettingsSelector.availableIciFieldNames)
   public availableIci$: Observable<string[]>;

   @Select(AgentGreetingSettingsState.isIciDisplayGrouped)
   public isIciDisplayGrouped$: Observable<boolean>;

   public filterDisplayValue$: Observable<string>;

   public clearIcon: IconDefinition = WellKnownIconsEnum.CLEAR.iconSmall;
   public uploadIcon: IconDefinition = faUpload;

   public isIciDisplayGroupedActiveIcon: IconDefinition = faExpandAlt;
   public isIciDisplayGroupedNonActiveIcon: IconDefinition = faCompressAlt;

   public connectedLists: CdkDropList[] = [];
   private destroy$ = new Subject();

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   dropAssigned(agentGreeting: AgentGreetingItem) {
      if (!this.store.selectSnapshot(AgentGreetingSettingsState.isIciDisplayGrouped)) {
         this.store.dispatch(new AddGreetingItem(agentGreeting));
         return;
      }
      const entries = this.store.selectSnapshot(AgentGreetingSettingsSelector.getGreetingGroup)(agentGreeting);
      this.store.dispatch(new AddGreetingItems(entries));
   }

   removeAssignment(agentGreeting: AgentGreetingItem) {
      if (!this.store.selectSnapshot(AgentGreetingSettingsState.isIciDisplayGrouped)) {
         this.store.dispatch(new RemoveGreetingItem(agentGreeting));
         return;
      }
      const entries = this.store.selectSnapshot(AgentGreetingSettingsSelector.getGreetingGroup)(agentGreeting);
      this.store.dispatch(new RemoveGreetingItems(entries));
   }

   expandIciGroup(event: AgentGreetingItemExpandedState) {
      // TODO: In the future could be expanded to only expand skill or Ici Group, needs some additional adjustments that I don't have time for at the moment.
      this.store.dispatch(new SetAgentGreetingIsIciDisplayGrouped(!event.isExpanded));
   }

   setIciDisplayIsGrouped(isGrouped: boolean) {
      this.store.dispatch(new SetAgentGreetingIsIciDisplayGrouped(!isGrouped));
   }

   waveDrop(event: CdkDragDrop<string[]>) {
      if (event.previousContainer === event.container) {
         moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      } else if (event.container.data.length > 0) {
         return;
      } else {
         copyArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
      }
   }

   removeWaveFile(wave: AudioFileInfo) {
      if (!_.isNil(wave?.id)) {
         const entries = this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingByWave)(wave.id);
         if (!_.isEmpty(entries)) {
            entries.forEach((entry) => {
               this.store.dispatch(new RemoveGreetingItem(entry));
            });
         }
      }
      this.store.dispatch(new RemoveGreetingWave(wave));
   }

   uploadGreeting(event: AudioFile) {
      const waveId = this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingWavesNextId);
      const wave: AgentGreetingWave = {
         id: waveId,
         name: event.name,
         content: event.content,
      };
      this.store.dispatch(new AddGreetingWave(wave));
   }

   searchTextChanged(value: string) {
      if (_.isEmpty(value)) {
         this.store.dispatch(new SetAgentGreetingSkillFilter(null));
      }
      this.store.dispatch(new SetAgentGreetingSkillFilter(value));
   }

   dragStarted(fileInfo: AudioFileInfo) {
      this.store.dispatch(new SetAgentGreetingDraggedItemKey(fileInfo));
   }

   dragEnded(fileInfo: AudioFileInfo) {
      this.store.dispatch(new SetAgentGreetingDraggedItemKey(null));
   }
}
